import { formatDayHeading, nextIsoDate, shiftIsoDate, todayIso } from '@/lib/calendar/calendarDates'
import { formatArrivalTime } from '@/lib/calendar/checkIn'

/** Days ahead when searching by guest name or phone. */
export const APPOINTMENT_LOOKUP_SEARCH_DAYS = 90

const INACTIVE_STATUSES = new Set(['cancelled', 'no_show'])

/**
 * @param {import('@/types/api').Appointment} appointment
 */
export function isOccupyingAppointment(appointment) {
  return !INACTIVE_STATUSES.has(appointment.status)
}

/**
 * @param {import('@/types/api').Appointment[]} appointments
 */
export function occupyingAppointments(appointments) {
  return appointments.filter(isOccupyingAppointment)
}

/**
 * @param {string} [query]
 */
export function isGuestSearchQuery(query) {
  return (query ?? '').trim().length >= 2
}

/**
 * @param {string} dateIso
 */
export function lookupRangeForDate(dateIso) {
  return { from: dateIso, to: nextIsoDate(dateIso) }
}

/**
 * @param {string} [fromDate]
 */
export function lookupRangeForGuestSearch(fromDate) {
  const from = fromDate ?? todayIso()

  return {
    from,
    to: shiftIsoDate(from, APPOINTMENT_LOOKUP_SEARCH_DAYS),
  }
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
export function appointmentGuestLabel(appointment) {
  return appointment.customer?.name ?? 'Guest'
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
export function appointmentServiceLabel(appointment) {
  const names = (appointment.services ?? [])
    .map((line) => line.service?.name)
    .filter(Boolean)

  return names.length > 0 ? names.join(', ') : 'Service'
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
export function appointmentLookupSummary(appointment) {
  const stylist = appointment.stylist?.name ?? appointment.services?.[0]?.stylist?.name
  const time = formatArrivalTime(appointment.starts_at)
  const date = appointment.starts_at.slice(0, 10)

  if (stylist) {
    return `${formatDayHeading(date)} · ${time} · ${stylist}`
  }

  return `${formatDayHeading(date)} · ${time}`
}

/**
 * @param {number} count
 * @param {string} dateIso
 */
export function dayOccupancyLabel(count, dateIso) {
  const day = formatDayHeading(dateIso)

  if (count === 0) {
    return `No active bookings on ${day}`
  }

  return `${count} active booking${count === 1 ? '' : 's'} on ${day}`
}
